import { takeEvery } from 'redux-saga/effects';

import * as AccountsTypes from './Accounts/types';
import * as ApprovalsTypes from './Approvals/types';
import * as ClientsActionDefinitions from './Clients/actionDefinitions';
import * as ClustersTypes from './Clusters/types';
import * as CompanyTypes from './Company/types';
import * as DataSourcesTypes from './DataSources/types';
import * as DimensionsTypes from './Dimensions/types';
import * as GeneralTypes from './General/types';
import * as IntacctTypes from './Intacct/types';
import * as ItemsTypes from './Items/types';
import * as SamlTypes from './Saml/types';
import * as TeamTypes from './Team/types';
import * as TimesheetTypes from './Timesheet/types';
import * as TimesheetEntriesTypes from './TimesheetEntries/actionDefinitions';
import * as TimesheetRowsTypes from './TimesheetRows/types';
import * as TimesheetsTypes from './Timesheets/types';
import * as UserTypes from './User/types';
import * as TimeTypesTypes from './TimeTypes/types';
import * as userActionsTypes from './UserActions/types';
import * as userConsentsTypes from './UserConsents/types';
import * as userNotificationSettingsTypes from './UserNotificationSettings/types';
import * as userSettingsTypes from './UserSettings/types';

import { deleteAccountRequestSaga, getAccountsRequestSaga, updateAccountRequestSaga, updateCalendarRequestSaga } from './Accounts/sagas';
import { approveRequestSaga, declineRequestSaga, getDoneApprovalsRequestSaga, getTodoApprovalsRequestSaga } from './Approvals/sagas';
import { getClientsRequestSaga } from './Clients/sagas';
import {
  deleteClusterNodeRequestSaga,
  getClustersRequestSaga,
  splitClusterAndAssignToEntryRequestSaga,
  updateClusterOverlapsRequestSaga,
  updateClusterRequestSaga,
  splitClusterRequestSaga,
  updateClusterNodeRequestSaga,
  splitClusterAndOpenClusterAssignModalSaga,
} from './Clusters/sagas';
import {
  deactivateCompanyRequestSaga,
  getCompanyLogoRequestSaga,
  getCompanyRequestSaga,
  selectCompanyEntityRequestSaga,
  updateCompanyRequestSaga,
} from './Company/sagas';
import { getDataSourcesRequestSaga, updateDataSourcesRequestSaga } from './DataSources/sagas';
import { getDimensionsRequestSaga } from './Dimensions/sagas';
import { getApprovalsCheckRequestSaga } from './General/sagas';
import {
  intacctLoginRequestSaga,
  intacctSyncRequestSaga,
  getSyncStatusRequestSaga,
  getCompanySyncStatusRequestSaga,
} from './Intacct/sagas';
import { getItemsRequestSaga } from './Items/sagas';
import { checkSamlAuthRequestSaga } from './Saml/sagas';
import { getProjectsOfClientRequestSaga, getProjectsRequestSaga } from './Projects/sagas';
import { getTasksOfProjectRequestSaga, getTasksRequestSaga } from './Tasks/sagas';
import { getTeamRequestSaga, inviteUserRequestSaga, bulkInviteRequestSaga } from './Team/sagas';
import {
  createTimesheetRequestSaga,
  deleteTimesheetRequestSaga,
  getTimesheetRequestSaga,
  recallTimesheetRequestSaga,
  submitTimesheetRequestSaga,
  updateTimesheetRequestSaga,
  duplicateTimesheetRequestSaga,
  saveTimesheetRequestAsyncSaga,
  saveTimesheetRequestAsyncSagaFailure,
  saveTimesheetRequestAsyncSagaSuccess,
  submitTimesheetRequestSagaAsync,
  submitTimesheetRequestSagaAsyncFailure,
  submitTimesheetRequestSagaAsyncSuccess,
} from './Timesheet/sagas';
import {
  createTimesheetEntryRequestSaga,
  deleteTimesheetEntryRequestSaga,
  getTimesheetEntriesRequestSaga,
  updateTimesheetEntryRequestSaga,
  createEntryAndAssignClusterRequestSaga,
  createEntryAndAssignNodeRequestSaga,
} from './TimesheetEntries/sagas';
import {
  createTimesheetRowRequestSaga,
  deleteTimesheetRowRequestSaga,
  getTimesheetRowRequestSaga,
  getTimesheetRowsRequestSaga,
  updateTimesheetRowRequestSaga,
  toggleRowBillableRequestSaga,
  updateTimesheetRowDimensionsRequestSaga,
} from './TimesheetRows/sagas';
import { getTimesheetsRequestSaga } from './Timesheets/sagas';
import { getTimeTypesRequestSaga } from './TimeTypes/sagas';
import {
  getUserRequestSaga,
  loginRequestSaga,
  logoutRequestSaga,
  protectionPwdRequestSaga,
  registerRequestSaga,
  updateUserRequestSaga,
  forgotPasswordRequestSaga,
  updatePasswordRequestSaga,
  registerEmployeeRequestSaga,
  verifyUserRequestSaga,
  resetPasswordRequestSaga,
  validateResetTokenRequestSaga,
  updateUserLastActiveTimesheetRequestSaga,
  registerResendEmailRequestSaga,
  activateMfaRequestSaga,
  deactivateMfaRequestSaga,
  verifyMfaRequestSaga,
  sendFallbackMfaCodeRequestSaga,
} from './User/sagas';
import { updateUserActionRequestSaga } from './UserActions/sagas';
import { createUserConsentRequestSaga } from './UserConsents/sagas';
import { getUserNotificationSettingsRequestSaga, updateUserNotificationSettingsRequestSaga } from './UserNotificationSettings/sagas';
import { getUserSettingsRequestSaga, updateUserSettingsRequestSaga } from './UserSettings/sagas';

import * as ProjectsActionDefinitions from './Projects/actionDefinitions';
import * as TasksActionDefinitions from './Tasks/actionDefinitions';

export function* watchSagas() {
  yield takeEvery(AccountsTypes.DELETE_ACCOUNT_REQUEST, deleteAccountRequestSaga);
  yield takeEvery(AccountsTypes.GET_ACCOUNTS_REQUEST, getAccountsRequestSaga);
  yield takeEvery(AccountsTypes.UPDATE_ACCOUNT_REQUEST, updateAccountRequestSaga);
  yield takeEvery(AccountsTypes.UPDATE_CALENDAR_REQUEST, updateCalendarRequestSaga);

  yield takeEvery(ApprovalsTypes.APPROVE_REQUEST, approveRequestSaga);
  yield takeEvery(ApprovalsTypes.DECLINE_REQUEST, declineRequestSaga);
  yield takeEvery(ApprovalsTypes.GET_DONE_APPROVALS_REQUEST, getDoneApprovalsRequestSaga);
  yield takeEvery(ApprovalsTypes.GET_TODO_APPROVALS_REQUEST, getTodoApprovalsRequestSaga);

  yield takeEvery(ClientsActionDefinitions.GET_CLIENTS_REQUEST, getClientsRequestSaga);

  yield takeEvery(ClustersTypes.DELETE_CLUSTER_NODE_REQUEST, deleteClusterNodeRequestSaga);
  yield takeEvery(ClustersTypes.GET_CLUSTERS_REQUEST, getClustersRequestSaga);
  yield takeEvery(ClustersTypes.SPLIT_CLUSTER_REQUEST, splitClusterRequestSaga);
  yield takeEvery(ClustersTypes.SPLIT_CLUSTER_AND_OPEN_CLUSTER_ASSIGN_MODAL_REQUEST, splitClusterAndOpenClusterAssignModalSaga);
  yield takeEvery(ClustersTypes.SPLIT_CLUSTER_AND_ASSIGN_TO_ENTRY_REQUEST, splitClusterAndAssignToEntryRequestSaga);
  yield takeEvery(ClustersTypes.UPDATE_CLUSTER_OVERLAPS_REQUEST, updateClusterOverlapsRequestSaga);
  yield takeEvery(ClustersTypes.UPDATE_CLUSTER_NODE_REQUEST, updateClusterNodeRequestSaga);
  yield takeEvery(ClustersTypes.UPDATE_CLUSTER_REQUEST, updateClusterRequestSaga);

  yield takeEvery(CompanyTypes.DEACTIVATE_COMPANY_REQUEST, deactivateCompanyRequestSaga);
  yield takeEvery(CompanyTypes.GET_COMPANY_REQUEST, getCompanyRequestSaga);
  yield takeEvery(CompanyTypes.GET_COMPANY_LOGO_REQUEST, getCompanyLogoRequestSaga);
  yield takeEvery(CompanyTypes.SELECT_COMPANY_ENTITY_REQUEST, selectCompanyEntityRequestSaga);
  yield takeEvery(CompanyTypes.UPDATE_COMPANY_REQUEST, updateCompanyRequestSaga);

  yield takeEvery(DataSourcesTypes.GET_DATASOURCES_REQUEST, getDataSourcesRequestSaga);
  yield takeEvery(DataSourcesTypes.UPDATE_DATASOURCES_REQUEST, updateDataSourcesRequestSaga);

  yield takeEvery(DimensionsTypes.GET_DIMENSIONS_REQUEST, getDimensionsRequestSaga);

  yield takeEvery(GeneralTypes.GET_APPROVALS_CHECK_REQUEST, getApprovalsCheckRequestSaga);

  yield takeEvery(IntacctTypes.INTACCT_LOGIN_REQUEST, intacctLoginRequestSaga);
  yield takeEvery(IntacctTypes.INTACCT_SYNC_REQUEST, intacctSyncRequestSaga);
  yield takeEvery(IntacctTypes.GET_SYNC_STATUS_REQUEST, getSyncStatusRequestSaga);
  yield takeEvery(IntacctTypes.GET_COMPANY_SYNC_STATUS_REQUEST, getCompanySyncStatusRequestSaga);

  yield takeEvery(ItemsTypes.GET_ITEMS_REQUEST, getItemsRequestSaga);

  yield takeEvery(ProjectsActionDefinitions.GET_PROJECTS_REQUEST, getProjectsRequestSaga);
  yield takeEvery(ProjectsActionDefinitions.GET_PROJECTS_OF_CLIENT_REQUEST, getProjectsOfClientRequestSaga);

  yield takeEvery(SamlTypes.CHECK_SAML_AUTH_REQUEST, checkSamlAuthRequestSaga);

  yield takeEvery(TasksActionDefinitions.GET_TASKS_REQUEST, getTasksRequestSaga);
  yield takeEvery(TasksActionDefinitions.GET_TASKS_OF_PROJECT_REQUEST, getTasksOfProjectRequestSaga);

  yield takeEvery(TeamTypes.BULK_INVITE_REQUEST, bulkInviteRequestSaga);
  yield takeEvery(TeamTypes.GET_TEAM_REQUEST, getTeamRequestSaga);
  yield takeEvery(TeamTypes.INVITE_USER_REQUEST, inviteUserRequestSaga);

  yield takeEvery(TimesheetTypes.CREATE_TIMESHEET_REQUEST, createTimesheetRequestSaga);
  yield takeEvery(TimesheetTypes.DELETE_TIMESHEET_REQUEST, deleteTimesheetRequestSaga);
  yield takeEvery(TimesheetTypes.DUPLICATE_TIMESHEET_REQUEST, duplicateTimesheetRequestSaga);
  yield takeEvery(TimesheetTypes.GET_TIMESHEET_REQUEST, getTimesheetRequestSaga);
  yield takeEvery(TimesheetTypes.RECALL_TIMESHEET_REQUEST, recallTimesheetRequestSaga);
  yield takeEvery(TimesheetTypes.SUBMIT_TIMESHEET_REQUEST, submitTimesheetRequestSaga);
  yield takeEvery(TimesheetTypes.UPDATE_TIMESHEET_REQUEST, updateTimesheetRequestSaga);
  yield takeEvery(TimesheetTypes.SUBMIT_TIMESHEET_REQUEST_ASYNC, submitTimesheetRequestSagaAsync);
  yield takeEvery(TimesheetTypes.SUBMIT_TIMESHEET_REQUEST_ASYNC_SUCCESS, submitTimesheetRequestSagaAsyncSuccess);
  yield takeEvery(TimesheetTypes.SUBMIT_TIMESHEET_REQUEST_ASYNC_FAILURE, submitTimesheetRequestSagaAsyncFailure);
  yield takeEvery(TimesheetTypes.SAVE_TIMESHEET_REQUEST_ASYNC, saveTimesheetRequestAsyncSaga);
  yield takeEvery(TimesheetTypes.SAVE_TIMESHEET_REQUEST_ASYNC_SUCCESS, saveTimesheetRequestAsyncSagaSuccess);
  yield takeEvery(TimesheetTypes.SAVE_TIMESHEET_REQUEST_ASYNC_FAILURE, saveTimesheetRequestAsyncSagaFailure);

  yield takeEvery(TimesheetEntriesTypes.CREATE_TIMESHEET_ENTRY_REQUEST, createTimesheetEntryRequestSaga);
  yield takeEvery(TimesheetEntriesTypes.CREATE_ENTRY_AND_ASSIGN_CLUSTER_REQUEST, createEntryAndAssignClusterRequestSaga);
  yield takeEvery(TimesheetEntriesTypes.CREATE_ENTRY_AND_ASSIGN_NODE_REQUEST, createEntryAndAssignNodeRequestSaga);
  yield takeEvery(TimesheetEntriesTypes.DELETE_TIMESHEET_ENTRY_REQUEST, deleteTimesheetEntryRequestSaga);
  yield takeEvery(TimesheetEntriesTypes.GET_TIMESHEET_ENTRIES_REQUEST, getTimesheetEntriesRequestSaga);
  yield takeEvery(TimesheetEntriesTypes.UPDATE_TIMESHEET_ENTRY_REQUEST, updateTimesheetEntryRequestSaga);

  yield takeEvery(TimesheetRowsTypes.CREATE_TIMESHEET_ROW_REQUEST, createTimesheetRowRequestSaga);
  yield takeEvery(TimesheetRowsTypes.DELETE_TIMESHEET_ROW_REQUEST, deleteTimesheetRowRequestSaga);
  yield takeEvery(TimesheetRowsTypes.GET_TIMESHEET_ROW_REQUEST, getTimesheetRowRequestSaga);
  yield takeEvery(TimesheetRowsTypes.GET_TIMESHEET_ROWS_REQUEST, getTimesheetRowsRequestSaga);
  yield takeEvery(TimesheetRowsTypes.TOGGLE_ROW_BILLABLE_REQUEST, toggleRowBillableRequestSaga);
  yield takeEvery(TimesheetRowsTypes.UPDATE_TIMESHEET_ROW_REQUEST, updateTimesheetRowRequestSaga);
  yield takeEvery(TimesheetRowsTypes.UPDATE_TIMESHEET_ROW_DIMENSIONS_REQUEST, updateTimesheetRowDimensionsRequestSaga);

  yield takeEvery(TimesheetsTypes.GET_TIMESHEETS_REQUEST, getTimesheetsRequestSaga);

  yield takeEvery(TimeTypesTypes.GET_TIME_TYPES_REQUEST, getTimeTypesRequestSaga);

  yield takeEvery(UserTypes.ACTIVATE_MFA_REQUEST, activateMfaRequestSaga);
  yield takeEvery(UserTypes.DEACTIVATE_MFA_REQUEST, deactivateMfaRequestSaga);
  yield takeEvery(UserTypes.VERIFY_MFA_REQUEST, verifyMfaRequestSaga);
  yield takeEvery(UserTypes.FORGOT_PASSWORD_REQUEST, forgotPasswordRequestSaga);
  yield takeEvery(UserTypes.GET_USER_REQUEST, getUserRequestSaga);
  yield takeEvery(UserTypes.LOGIN_REQUEST, loginRequestSaga);
  yield takeEvery(UserTypes.LOGOUT_REQUEST, logoutRequestSaga);
  yield takeEvery(UserTypes.REGISTER_REQUEST, registerRequestSaga);
  yield takeEvery(UserTypes.REGISTER_RESEND_EMAIL_REQUEST, registerResendEmailRequestSaga);
  yield takeEvery(UserTypes.REGISTER_EMPLOYEE_REQUEST, registerEmployeeRequestSaga);
  yield takeEvery(UserTypes.RESET_PASSWORD_REQUEST, resetPasswordRequestSaga);
  yield takeEvery(UserTypes.SEND_FALLBACK_MFA_CODE_REQUEST, sendFallbackMfaCodeRequestSaga);
  yield takeEvery(UserTypes.PROTECTION_PWD_REQUEST, protectionPwdRequestSaga);
  yield takeEvery(UserTypes.UPDATE_PASSWORD_REQUEST, updatePasswordRequestSaga);
  yield takeEvery(UserTypes.UPDATE_USER_LAST_ACTIVE_TIMESHEET_REQUEST, updateUserLastActiveTimesheetRequestSaga);
  yield takeEvery(UserTypes.UPDATE_USER_REQUEST, updateUserRequestSaga);
  yield takeEvery(UserTypes.VALIDATE_RESET_TOKEN_REQUEST, validateResetTokenRequestSaga);
  yield takeEvery(UserTypes.VERIFY_USER_REQUEST, verifyUserRequestSaga);

  yield takeEvery(userActionsTypes.UPDATE_USER_ACTION_REQUEST, updateUserActionRequestSaga);

  yield takeEvery(userConsentsTypes.CREATE_USER_CONSENT_REQUEST, createUserConsentRequestSaga);

  yield takeEvery(userNotificationSettingsTypes.GET_USER_NOTIFICATION_SETTINGS_REQUEST, getUserNotificationSettingsRequestSaga);
  yield takeEvery(userNotificationSettingsTypes.UPDATE_USER_NOTIFICATION_SETTINGS_REQUEST, updateUserNotificationSettingsRequestSaga);

  yield takeEvery(userSettingsTypes.GET_USER_SETTINGS_REQUEST, getUserSettingsRequestSaga);
  yield takeEvery(userSettingsTypes.UPDATE_USER_SETTINGS_REQUEST, updateUserSettingsRequestSaga);
}
